import React, { useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { useParams } from "react-router-dom";

import { apiDebugLog, apiGet } from "../../api";
import { useAuth } from "../../auth/AuthProvider";
import ExternalButton from "../../components/BaseButton/ExternalButton";
import { useNavigationHeader } from "../../components/MainLayout";
import PageHeader from "../../components/PageHeader";
import { useAppData } from "../../data/AppDataProvider";
import styles from "./Instruction.module.css";

type OSKey = "android" | "ios" | "windows" | "macos" | "linux" | "androidtv" | "appletv";

type OSListItem = {
    key: OSKey;
    label: string;
    icon: string;
};

type InstructionData = {
    downloadLinks: Record<string, { btns?: string[] } | undefined>;
    importLink: string;
};

type InstructionStatus = {
    isLoading: boolean;
    error?: string;
};

type NavigationHeaderData = {
    shouldShowNavigation: boolean;
    setBottomActions: (actions: React.ReactNode | null) => void;
};

type AuthData = {
    isTelegram?: boolean;
} | null;

type InstructionContentProps = {
    auth: AuthData;
    data: InstructionData;
    status: InstructionStatus;
    pageTitle: string;
    hideInstruction: boolean;
};

type DownloadButton = {
    icon: string;
    text: string;
    href: string;
};

type AppearStyle = React.CSSProperties & {
    "--appear-delay": string;
};

type NavigatorWithUserAgentData = Navigator & {
    userAgentData?: {
        platform?: string;
    };
};

type TelegramWindow = Window & typeof globalThis & {
    Telegram?: {
        WebApp?: {
            openLink?: (url: string, options?: { try_browser?: string }) => void;
        };
    };
};

const OS_LIST: OSListItem[] = [
    { key: "android", label: "Android", icon: "#icon-android" },
    { key: "ios", label: "iOS", icon: "#icon-ios" },
    { key: "windows", label: "Windows", icon: "#icon-windows" },
    { key: "macos", label: "MacOS", icon: "#icon-macos" },
    { key: "linux", label: "Linux", icon: "#icon-linux" },
    { key: "androidtv", label: "Android TV", icon: "#icon-androidtv" },
    { key: "appletv", label: "Apple TV", icon: "#icon-appletv" },
];

const OS_CONFIG: Record<string, { description: string }> = {
    android: {
        description: "Установите приложение из Google Play, либо по прямой ссылке, воспользовавшись кнопками ниже.",
    },
    ios: {
        description: "Установите приложение Incy из AppStore, воспользовавшись кнопками ниже.",
    },
    windows: {
        description: "Установите приложение Incy, воспользовавшись кнопками ниже.",
    },
    macos: {
        description: "Установите приложение Incy из AppStore.",
    },
    linux: {
        description: "Установите приложение Incy",
    },
};

const INITIAL_PUBLIC_DATA: InstructionData = {
    downloadLinks: {},
    importLink: "",
};

function parseDownloadButton(value: unknown): DownloadButton | null {
    if (typeof value !== "string") {
        return null;
    }

    const [icon = "", text = "", href = ""] = value.split(";");
    const normalizedText = text.trim();

    if (!normalizedText) {
        return null;
    }

    return {
        icon: icon.trim() || "icon-download",
        text: normalizedText,
        href: href.trim(),
    };
}

function detectCurrentOS(): OSKey {
    if (typeof window === "undefined") {
        return "android";
    }

    const platform =
        (window.navigator as NavigatorWithUserAgentData).userAgentData?.platform ||
        window.navigator.platform ||
        window.navigator.userAgent ||
        "";
    const normalizedPlatform = platform.toLowerCase();

    if (/android/.test(normalizedPlatform)) {
        return "android";
    }

    if (/iphone|ipad|ipod|ios/.test(normalizedPlatform)) {
        return "ios";
    }

    if (/win/.test(normalizedPlatform)) {
        return "windows";
    }

    if (/mac/.test(normalizedPlatform)) {
        return "macos";
    }

    if (/linux|x11|ubuntu|cros/.test(normalizedPlatform)) {
        return "linux";
    }

    return "android";
}

function summarizeImportLink(importLink: unknown) {
    const normalizedLink = typeof importLink === "string" ? importLink.trim() : "";
    const [scheme = ""] = normalizedLink.split("://", 1);

    return {
        has_import_link: Boolean(normalizedLink),
        import_link_length: normalizedLink.length,
        import_link_scheme: scheme || null,
        import_link_prefix: normalizedLink.slice(0, 48) || null,
        import_link_suffix: normalizedLink.slice(-24) || null,
    };
}

function summarizeEnvironment() {
    if (typeof window === "undefined") {
        return {
            has_window: false,
            has_telegram_webapp: false,
            user_agent: null,
        };
    }

    const telegramWindow = window as TelegramWindow;

    return {
        has_window: true,
        has_telegram_webapp: Boolean(telegramWindow.Telegram?.WebApp),
        user_agent: window.navigator?.userAgent || null,
    };
}

function buildIncyRedirectUrl(importLink: unknown) {
    const normalizedLink = typeof importLink === "string" ? importLink.trim() : "";
    if (!normalizedLink) {
        return "";
    }

    return `https://core.sodafizz.cool/utils/incy?link=${encodeURIComponent(normalizedLink)}`;
}

function InstructionContent({ auth, data, status, pageTitle, hideInstruction }: InstructionContentProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [os, setOs] = useState(detectCurrentOS);
    const selectRef = useRef<HTMLDivElement | null>(null);
    const { shouldShowNavigation, setBottomActions } = useNavigationHeader() as NavigationHeaderData;

    const currentOS = OS_LIST.find((item) => item.key === os) || OS_LIST[0];
    const current = OS_CONFIG[os] || OS_CONFIG.android;
    const osDownloadButtons = data.downloadLinks?.[os]?.btns;
    const downloadButtons = Array.isArray(osDownloadButtons)
        ? osDownloadButtons.map(parseDownloadButton).filter((button): button is DownloadButton => Boolean(button))
        : [];
    const importLink = data.importLink || "";
    const useRedirectImport = Boolean(auth?.isTelegram);
    const importTargetUrl = useRedirectImport ? buildIncyRedirectUrl(importLink) : importLink;
    const { isLoading, error } = status;
    const getAppearStyle = (): AppearStyle => ({ "--appear-delay": `${(0.08 + Math.random() * 0.18).toFixed(3)}s` });

    const supportAction = useMemo(() => (
        <ExternalButton
            icon="icon-support"
            href="https://t.me/sodavpn_support"
            className="app-bottom-nav-btn"
            target="_blank"
            rel="noreferrer"
        >
            <span>Написать в поддержку</span>
        </ExternalButton>
    ), []);

    useEffect(() => {
        const handleClickOutside = (event: globalThis.MouseEvent) => {
            if (selectRef.current && event.target instanceof Node && !selectRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (!shouldShowNavigation || hideInstruction) {
            setBottomActions(null);
            return undefined;
        }

        setBottomActions(supportAction);

        return () => {
            setBottomActions(null);
        };
    }, [hideInstruction, setBottomActions, shouldShowNavigation, supportAction]);

    useEffect(() => {
        const debugPayload = {
            os,
            is_telegram: Boolean(auth?.isTelegram),
            is_loading: isLoading,
            has_error: Boolean(error),
            ...summarizeEnvironment(),
            import_target_prefix: importTargetUrl.slice(0, 64) || null,
            ...summarizeImportLink(importLink),
        };

        console.warn("instruction_screen_state", debugPayload);
        void apiDebugLog("instruction_screen_state", debugPayload);
    }, [auth?.isTelegram, error, importLink, importTargetUrl, isLoading, os]);

    const handleImportClick = (event: MouseEvent<HTMLAnchorElement>) => {
        const debugPayload = {
            os,
            is_telegram: Boolean(auth?.isTelegram),
            is_loading: isLoading,
            has_error: Boolean(error),
            ...summarizeEnvironment(),
            import_target_prefix: importTargetUrl.slice(0, 64) || null,
            ...summarizeImportLink(importLink),
        };

        console.warn("instruction_import_click", debugPayload);
        void apiDebugLog("instruction_import_click", debugPayload);

        if (isLoading || !importLink || !importTargetUrl || typeof window === "undefined") {
            event.preventDefault();
            return;
        }

        try {
            const telegramWindow = window as TelegramWindow;

            if (useRedirectImport && telegramWindow.Telegram?.WebApp?.openLink) {
                event.preventDefault();
                telegramWindow.Telegram.WebApp.openLink(importTargetUrl, {
                    try_browser: "chrome",
                });
                return;
            }
        } catch (launchError) {
            event.preventDefault();
            const launchPayload = {
                ...debugPayload,
                message: launchError instanceof Error ? launchError.message : String(launchError),
            };
            console.error("instruction_import_launch_failed", launchPayload);
            void apiDebugLog("instruction_import_launch_failed", launchPayload);
        }
    };

    const osSelector = (
        <div className={styles.osSelect} ref={selectRef}>
            <div className={`${styles.select} ${isOpen ? styles.open : ""} relative`}>
                <button
                    type="button"
                    className={`${styles.selectTrigger} flex-between align-center`}
                    onClick={(event) => {
                        event.stopPropagation();
                        setIsOpen(!isOpen);
                    }}
                >
                    <span className={`${styles.selectLeft} flex align-center`}>
                        <svg className={styles.icon} style={{ width: 18, height: 18 }}>
                            <use href={currentOS.icon}></use>
                        </svg>
                        <span className="text" style={{ marginTop: 2 }}>
                            {currentOS.label}
                        </span>
                    </span>

                    <span
                        className={`${styles.selectArrows} flex-col`}
                        style={{ transition: "transform 0.25s" }}
                    >
                        <svg
                            className={`${styles.arrow} ${styles.arrowUp}`}
                            style={{
                                transform: isOpen ? "rotate(-180deg)" : "rotate(0deg)",
                                transition: "transform 0.25s",
                            }}
                        >
                            <use href="#icon-arrow-up"></use>
                        </svg>
                        <svg
                            className={`${styles.arrow} ${styles.arrowDown}`}
                            style={{
                                transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                                transition: "transform 0.25s",
                            }}
                        >
                            <use href="#icon-arrow-down"></use>
                        </svg>
                    </span>
                </button>

                <div className={`${styles.selectDropdown} font08 absolute ${isOpen ? styles.open : ""}`}>
                    {OS_LIST.map((item) => (
                        <div
                            key={item.key}
                            className={`${styles.option} flex align-center pad1`}
                            onClick={() => {
                                setOs(item.key);
                                setIsOpen(false);
                            }}
                        >
                            <svg className={styles.icon} style={{ width: 18, height: 18 }}>
                                <use href={item.icon}></use>
                            </svg>
                            <span style={{ marginTop: 2 }}>{item.label}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    return (
        <>
            <div className={styles.header}>
                <PageHeader title={pageTitle} className={styles.pageHeader} />
                {!hideInstruction ? (
                    <span className={styles.subtitle}>Следуйте инструкции, чтобы настроить VPN на устройстве.</span>
                ) : null}
            </div>
            {hideInstruction ? (
                <div className="gray-block pad1 shadow-1 menu-appear" style={{ ...getAppearStyle(), marginTop: "0.8em" }}>
                    <span>{error}</span>
                </div>
            ) : (
                <>
            <div className={`default_block default_block_padded flex-between align-center ${styles.osRow}`}>
                <p>Операционная система</p> {osSelector}
            </div>
            <div className={`flex-col ${styles.steps}`}>
                {error ? (
                    <div className="gray-block pad1 shadow-1 menu-appear" style={{ ...getAppearStyle(), marginBottom: "1em" }}>
                        <span>{error}</span>
                    </div>
                ) : null}

                <div className={`${styles.step} menu-appear`} style={getAppearStyle()}>
                    <div className={`${styles.stepMarker} flex-col align-center`}>
                        <span className={`${styles.circle} flex-center`}>
                            1
                        </span>
                        <span className={styles.line}></span>
                    </div>

                    <div className={styles.stepContent}>
                        <h3 className={`font11 ${styles.stepTitle}`}>
                            Установите Incy
                        </h3>

                        <p className="font08 grey" style={{ margin: "0 0 8px" }}>
                            {current.description}
                        </p>

                        <div
                            className="attention-block font08"
                            style={{ padding: "8px 10px 10px" }}
                        >
                            <span>Важно!<br />После установки вернитесь сюда и выполните следующий шаг.</span>
                        </div>
                        <div className="flex-col gap05">
                            {downloadButtons.map((button, index) => (
                                <ExternalButton
                                    key={`${button.text}-${index}`}
                                    icon={button.icon}
                                    href={button.href || "#"}
                                    target="_blank"
                                    rel="noreferrer"
                                    disabled={isLoading || !button.href}
                                    className={`${styles.instructionButton} menu-appear`}
                                    style={getAppearStyle()}
                                >
                                    <span>{isLoading ? "Загрузка..." : button.text}</span>
                                </ExternalButton>
                            ))}
                        </div>
                    </div>
                </div>

                <div className={`${styles.step} menu-appear`} style={getAppearStyle()}>
                    <div className={`${styles.stepMarker} flex-col align-center`}>
                        <span className={`${styles.circle} flex-center`}>
                            2
                        </span>
                        <span className={styles.line}></span>
                    </div>
                    <div className={styles.stepContent}>
                        <h3 className={`font11 ${styles.stepTitle}`}>
                            Импортируйте сервера
                        </h3>

                        <p className="font08 grey" style={{ margin: "0 0 8px" }}>
                            Нажмите кнопку ниже, чтобы добавить сервера в приложение
                        </p>

                        <ExternalButton
                            icon="icon-import"
                            href={importTargetUrl || "#"}
                            target={useRedirectImport ? "_blank" : undefined}
                            rel={useRedirectImport ? "noreferrer" : undefined}
                            disabled={isLoading || !importLink}
                            onClick={handleImportClick}
                            className={`${styles.instructionButton} menu-appear`}
                            style={getAppearStyle()}
                        >
                            <span>{isLoading ? "Загрузка..." : "Импортировать VPN"}</span>
                        </ExternalButton>
                    </div>
                </div>

                <div className={`${styles.step} menu-appear`} style={getAppearStyle()}>
                    <div className={`${styles.stepMarker} flex-col align-center`}>
                        <span className={`${styles.circle} flex-center`}>
                            3
                        </span>
                    </div>

                    <div className={styles.stepContent}>
                        <h3 className={`font11 ${styles.stepTitle}`}>
                            Готово!
                        </h3>

                        <p className="font08 grey" style={{ margin: "0 0 8px" }}>
                            Теперь вы можете выбрать сервер и использовать VPN
                        </p>
                    </div>
                </div>
            </div>
                </>
            )}
        </>
    );
}

export default function Instruction() {
    const auth = useAuth();
    const { instructionData, instructionStatus } = useAppData() as {
        instructionData: InstructionData;
        instructionStatus: InstructionStatus;
    };
    const { token } = useParams();
    const isSharedMode = Boolean(token);
    const [sharedData, setSharedData] = useState(INITIAL_PUBLIC_DATA);
    const [sharedStatus, setSharedStatus] = useState<InstructionStatus>({ isLoading: false, error: "" });

    useEffect(() => {
        if (!isSharedMode || !token) {
            setSharedData(INITIAL_PUBLIC_DATA);
            setSharedStatus({ isLoading: false, error: "" });
            return;
        }

        let isCancelled = false;
        const sharedToken = token;

        async function loadSharedInstruction() {
            setSharedStatus({ isLoading: true, error: "" });

            try {
                const response = await apiGet(`/utils/instruction_page_data/${encodeURIComponent(sharedToken)}`) as Partial<InstructionData>;

                if (isCancelled) {
                    return;
                }

                setSharedData({
                    downloadLinks: response.downloadLinks && typeof response.downloadLinks === "object"
                        ? response.downloadLinks
                        : {},
                    importLink: response.importLink || "",
                });
                setSharedStatus({ isLoading: false, error: "" });
            } catch (error: unknown) {
                if (isCancelled) {
                    return;
                }

                const errorStatus = typeof error === "object" && error !== null && "status" in error ? Number(error.status) : null;
                const errorMessage = errorStatus === 410
                    ? "Ссылка истекла"
                    : "Ссылка недействительна";

                setSharedData(INITIAL_PUBLIC_DATA);
                setSharedStatus({ isLoading: false, error: errorMessage });
            }
        }

        void loadSharedInstruction();

        return () => {
            isCancelled = true;
        };
    }, [isSharedMode, token]);

    const data = isSharedMode ? sharedData : instructionData;
    const status = isSharedMode ? sharedStatus : instructionStatus;
    const hideInstruction = isSharedMode && Boolean(sharedStatus.error);
    const pageTitle = isSharedMode ? "Подключение VPN" : "Установка";

    return (
        <InstructionContent
            auth={isSharedMode ? null : auth}
            data={data}
            status={status}
            pageTitle={pageTitle}
            hideInstruction={hideInstruction}
        />
    );
}
